/**
 * Notification Bell
 * Header bell showing recent @mentions with an unread-count badge.
 * Clicking a mention navigates to the originating channel/DM and marks it read.
 * Built on the existing mentions API (no separate notifications endpoint).
 */

import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import {
  useMentions,
  useUnreadMentionCounts,
  useMarkMentionRead,
  useMarkConversationMentionsRead,
} from '@/hooks/useMentions';
import { useAppStore } from '@/store/app.store';
import Avatar from '@/components/common/Avatar';
import type { Mention } from '@/types/api.types';

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Току-що';
  if (diffMin < 60) return `Преди ${diffMin} мин`;
  if (diffHours < 24) return `Преди ${diffHours} ч`;
  if (diffDays < 7) return `Преди ${diffDays} дни`;
  return date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'short' });
};

export const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { setActiveChannel, setActiveDMThread } = useAppStore();
  const { data: mentionsData } = useMentions({ limit: 20 });
  const { data: unreadCounts } = useUnreadMentionCounts();
  const markRead = useMarkMentionRead();
  const markAllRead = useMarkConversationMentionsRead();

  const mentions = mentionsData?.mentions ?? [];
  const unreadTotal = unreadCounts?.total ?? 0;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMentionClick = (mention: Mention) => {
    if (!mention.is_read) {
      markRead.mutate(mention.id);
    }
    if (mention.channel_id) {
      setActiveChannel(mention.channel_id);
    } else if (mention.dm_thread_id) {
      setActiveDMThread(mention.dm_thread_id);
    }
    setOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate({});
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`p-2 rounded-lg transition-colors relative ${
          open
            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
            : 'hover:bg-gray-100 dark:hover:bg-dark-bg-elevated text-gray-600 dark:text-dark-text-secondary'
        }`}
        title="Известия"
        aria-label={unreadTotal > 0 ? `Известия, ${unreadTotal} непрочетени` : 'Известия'}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" />
        {unreadTotal > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 max-h-[28rem] flex flex-col bg-white dark:bg-dark-bg-elevated border border-gray-100 dark:border-dark-border-secondary rounded-xl shadow-lg z-50 animate-modal-in overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-dark-border-secondary">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
              Известия
            </h3>
            {unreadTotal > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markAllRead.isPending}
                className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
              >
                Маркирай всички като прочетени
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {mentions.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto w-8 h-8 text-gray-300 dark:text-dark-text-muted mb-2" />
                <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                  Нямате нови споменавания
                </p>
              </div>
            ) : (
              mentions.map((mention) => {
                const byName =
                  mention.mentionedBy?.display_name ||
                  `${mention.mentionedBy?.first_name ?? ''} ${mention.mentionedBy?.last_name ?? ''}`.trim() ||
                  'Някой';
                const snippet = mention.message?.content ? stripHtml(mention.message.content) : '';
                return (
                  <button
                    key={mention.id}
                    onClick={() => handleMentionClick(mention)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-input transition-colors border-b border-gray-50 dark:border-dark-border-secondary/50 ${
                      mention.is_read ? '' : 'bg-primary-50/50 dark:bg-primary-900/10'
                    }`}
                  >
                    {mention.mentionedBy && (
                      <Avatar user={mention.mentionedBy} size="sm" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 dark:text-dark-text-primary">
                        <span className="font-semibold">{byName}</span>
                        <span className="text-gray-500 dark:text-dark-text-muted"> ви спомена </span>
                        <span className="text-gray-500 dark:text-dark-text-muted">
                          {mention.channel_id ? 'в канал' : 'в лични съобщения'}
                        </span>
                      </p>
                      {snippet && (
                        <p className="text-xs text-gray-500 dark:text-dark-text-muted truncate mt-0.5">
                          {snippet}
                        </p>
                      )}
                      <p className="text-[11px] text-gray-400 dark:text-dark-text-muted mt-0.5">
                        {formatRelativeTime(mention.created_at)}
                      </p>
                    </div>
                    {!mention.is_read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
